import { useRef, useEffect } from "react";

// ── Simplex noise (Stefan Gustavson / Peter Eastman, public domain) ──────────

function makeSimplex(seed) {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = (seed >>> 0) ^ 0xdeadbeef;
  for (let i = 255; i > 0; i--) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    const j = s % (i + 1);
    const tmp = p[i]; p[i] = p[j]; p[j] = tmp;
  }
  const perm = new Uint8Array(512);
  const permMod12 = new Uint8Array(512);
  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    permMod12[i] = perm[i] % 12;
  }
  const G = new Float32Array([
     1, 1, -1, 1,  1,-1, -1,-1,
     1, 0, -1, 0,  0, 1,  0,-1,
     1, 1, -1, 1,  1,-1, -1,-1,
  ]);
  const F2 = (Math.sqrt(3) - 1) / 2;
  const G2 = (3 - Math.sqrt(3)) / 6;

  return function noise2D(x, y) {
    const s  = (x + y) * F2;
    const i  = Math.floor(x + s);
    const j  = Math.floor(y + s);
    const t  = (i + j) * G2;
    const x0 = x - (i - t);
    const y0 = y - (j - t);
    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;
    const ii  = i & 255;
    const jj  = j & 255;
    const gi0 = permMod12[ii      + perm[jj     ]] * 2;
    const gi1 = permMod12[ii + i1 + perm[jj + j1]] * 2;
    const gi2 = permMod12[ii + 1  + perm[jj + 1 ]] * 2;
    let n = 0;
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) { t0 *= t0; n += t0 * t0 * (G[gi0] * x0 + G[gi0 + 1] * y0); }
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) { t1 *= t1; n += t1 * t1 * (G[gi1] * x1 + G[gi1 + 1] * y1); }
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) { t2 *= t2; n += t2 * t2 * (G[gi2] * x2 + G[gi2 + 1] * y2); }
    return 70 * n;
  };
}

// ── Config ───────────────────────────────────────────────────────────────────

const CFG = {
  particleCount:   10000,
  lifeMin:         100,
  lifeMax:         260,
  noiseScale:      0.0018,
  noiseSpeed:      0.00022,
  fieldStrength:   2.4,
  curlEps:         1.5,
  trailDecay:      0.016,
  bgR: 0, bgG: 0, bgB: 8,
  lineWidth:       1.0,
  baseAlpha:       0.55,
  // Fixed blue hue — no drift, no velocity-based mapping
  fixedHue:        215,
  colorSaturation: 88,
  colorLightness:  62,
  driftAngle:      200,
  driftSpeed:      0.6,
};

// ── Vector field ─────────────────────────────────────────────────────────────

class VectorField {
  constructor() {
    this.noise  = makeSimplex(Date.now());
    this.time   = 0;
  }

  _curl(x, y) {
    const e = CFG.curlEps;
    const s = CFG.noiseScale;
    const t = this.time;
    const dny = (this.noise(x * s, (y + e) * s + t) -
                 this.noise(x * s, (y - e) * s + t)) / (2 * e);
    const dnx = (this.noise((x + e) * s, y * s + t) -
                 this.noise((x - e) * s, y * s + t)) / (2 * e);
    return { x: dny * CFG.fieldStrength, y: -dnx * CFG.fieldStrength };
  }

  sample(x, y) {
    const v = this._curl(x, y);
    const rad = CFG.driftAngle * (Math.PI / 180);
    v.x += Math.cos(rad) * CFG.driftSpeed;
    v.y += Math.sin(rad) * CFG.driftSpeed;
    return v;
  }

  tick(dt) {
    this.time += CFG.noiseSpeed * dt;
  }
}

// ── Particle system ───────────────────────────────────────────────────────────

class ParticleSystem {
  constructor(count, w, h) {
    this.count = count;
    this.w = w; this.h = h;
    this.x    = new Float32Array(count);
    this.y    = new Float32Array(count);
    this.px   = new Float32Array(count);
    this.py   = new Float32Array(count);
    this.age  = new Float32Array(count);
    this.life = new Float32Array(count);
    for (let i = 0; i < count; i++) this._spawn(i, true);
  }

  _spawn(i, scatterAge = false) {
    const x = Math.random() * this.w;
    const y = Math.random() * this.h;
    this.x[i] = this.px[i] = x;
    this.y[i] = this.py[i] = y;
    const life = CFG.lifeMin + Math.random() * (CFG.lifeMax - CFG.lifeMin);
    this.life[i] = life;
    this.age[i]  = scatterAge ? Math.random() * life : 0;
  }

  resize(w, h) {
    this.w = w; this.h = h;
    for (let i = 0; i < this.count; i++) this._spawn(i, true);
  }

  update(field, dt) {
    const W = this.w, H = this.h;
    for (let i = 0; i < this.count; i++) {
      this.px[i] = this.x[i];
      this.py[i] = this.y[i];
      const v = field.sample(this.x[i], this.y[i]);
      this.x[i]  += v.x * dt;
      this.y[i]  += v.y * dt;
      this.age[i] += dt;
      if (this.age[i] >= this.life[i] ||
          this.x[i] < -4 || this.x[i] > W + 4 ||
          this.y[i] < -4 || this.y[i] > H + 4) {
        this._spawn(i, false);
      }
    }
  }
}

// ── Renderer ──────────────────────────────────────────────────────────────────

class Renderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: false });
    this.resize();
  }

  resize() {
    this.canvas.width  = window.innerWidth;
    this.canvas.height = window.innerHeight;
    const { ctx, canvas } = this;
    ctx.fillStyle = `rgb(${CFG.bgR},${CFG.bgG},${CFG.bgB})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  drawFrame(ps) {
    const { ctx, canvas } = this;
    const W = canvas.width, H = canvas.height;

    // Trail fade
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = `rgba(${CFG.bgR},${CFG.bgG},${CFG.bgB},${CFG.trailDecay})`;
    ctx.fillRect(0, 0, W, H);

    // Particles — single fixed blue hue, additive blending
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = CFG.lineWidth;
    ctx.strokeStyle = `hsla(${CFG.fixedHue},${CFG.colorSaturation}%,${CFG.colorLightness}%,${CFG.baseAlpha})`;

    ctx.beginPath();
    const N = ps.count;
    for (let i = 0; i < N; i++) {
      const t = ps.age[i] / ps.life[i];
      if (t < 0.02 || t > 0.98) continue;
      ctx.moveTo(ps.px[i], ps.py[i]);
      ctx.lineTo(ps.x[i],  ps.y[i]);
    }
    ctx.stroke();

    ctx.globalCompositeOperation = 'source-over';
  }
}

// ── React component ───────────────────────────────────────────────────────────

export default function VectorFieldBackground({ opacity = 1 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const field    = new VectorField();
    const renderer = new Renderer(canvas);
    const ps       = new ParticleSystem(CFG.particleCount, canvas.width, canvas.height);

    let lastTime = performance.now();
    let rafId;

    const frame = (now) => {
      const dt = Math.min((now - lastTime) / 16.667, 3);
      lastTime = now;
      field.tick(dt);
      ps.update(field, dt);
      renderer.drawFrame(ps);
      rafId = requestAnimationFrame(frame);
    };

    rafId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        pointerEvents: 'none',
        opacity,
      }}
    />
  );
}
